// Per-tenant config defaults + the touchpoint proof gate (pure, unit-tested).
export const DEFAULT_CONFIG = {
  proofPolicy: 'optional', // 'optional' | 'required'
  requireNextStep: false,
  defaultSortDir: 'desc', // 'asc' | 'desc'
  // Customizable in Settings (per tenant). "channels" replaces the old "category".
  tiers: ['Anchor', 'Mid', 'Small'],
  channels: ['Hotel', 'Spa', 'Gym', 'Restaurant', 'Salon', 'Clinic'],
  noteReasonCodes: {
    Status: ['Contacted', 'Interested', 'Not interested', 'Follow-up set'],
    Action: ['Intro email', 'Quote sent', 'Site visit', 'Sample pickup'],
  },
};

export function withDefaults(config) {
  return { ...DEFAULT_CONFIG, ...(config || {}) };
}

// A signature only makes sense for a physical touchpoint. Remote channels
// (SMS/Viber/Email/Phone/Call) can't be signed, so their proof is a screenshot
// or written notes instead. Keep this in sync with VISIT_CHANNELS.
export const VISIT_TOUCH_CHANNELS = ['In-person', 'Visit'];
export const isVisitChannel = (channel) => VISIT_TOUCH_CHANNELS.includes(channel);

// What counts as "proof" for a touch depends on the channel:
//   • visit channel  → a signature OR a photo
//   • remote channel → a screenshot (photo) OR written notes
// A photo always counts. Signature only counts on a visit; notes only count
// on a remote channel (a visit is expected to be signed, not just described).
export function hasTouchProof(touch = {}) {
  if (touch.hasPhoto) return true;
  return isVisitChannel(touch.channel) ? Boolean(touch.hasSignature) : Boolean(touch.hasText);
}

// Gate for logging a touch ("Log Touchpoint"). Returns { ok, errors }.
// proofPolicy=required blocks save without proof; requireNextStep blocks save
// without a next step. Both enforce client + server-policy parity.
// Back-compat: callers may pass `hasProof` directly; otherwise proof is derived
// channel-aware from hasPhoto/hasSignature/hasText via hasTouchProof().
export function validateTouch(config, touch = {}) {
  const c = withDefaults(config);
  const errors = [];
  const hasProof = touch.hasProof !== undefined ? touch.hasProof : hasTouchProof(touch);
  if (c.proofPolicy === 'required' && !hasProof) {
    errors.push(isVisitChannel(touch.channel)
      ? 'Proof (a signature or photo) is required to log this visit.'
      : 'Proof (a screenshot or notes) is required to log this touchpoint.');
  }
  if (c.requireNextStep && !touch.nextStep) {
    errors.push('A next step is required.');
  }
  return { ok: errors.length === 0, errors };
}
